import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '../api/client';

const empty = { currentPassword: '', newPassword: '', confirm: '' };

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState(empty);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const change = useMutation({
    mutationFn: async () =>
      (await api.post('/auth/change-password', {
        currentPassword: form.currentPassword, newPassword: form.newPassword,
      })).data.data,
    onSuccess: () => setForm(empty),
  });

  const mismatch = form.confirm.length > 0 && form.newPassword !== form.confirm;
  const tooShort = form.newPassword.length > 0 && form.newPassword.length < 8;
  const errMsg =
    (change.error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
    'Failed to change password';

  const handleClose = () => { setForm(empty); change.reset(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Change Password</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Current Password" type="password" value={form.currentPassword} onChange={(e) => set('currentPassword', e.target.value)} fullWidth />
          <TextField
            label="New Password" type="password" value={form.newPassword} onChange={(e) => set('newPassword', e.target.value)} fullWidth
            error={tooShort} helperText={tooShort ? 'At least 8 characters.' : ' '}
          />
          <TextField
            label="Confirm New Password" type="password" value={form.confirm} onChange={(e) => set('confirm', e.target.value)} fullWidth
            error={mismatch} helperText={mismatch ? 'Passwords do not match.' : ' '}
          />
          {change.isError && <Alert severity="error">{errMsg}</Alert>}
          {change.isSuccess && <Alert severity="success">Password changed successfully.</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">Close</Button>
        <Button
          variant="contained"
          onClick={() => change.mutate()}
          disabled={change.isPending || !form.currentPassword || form.newPassword.length < 8 || mismatch || !form.confirm}
        >
          {change.isPending ? 'Saving…' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
